import { useMemo } from 'react';

import { Configs } from 'utils/constants';
import { getI18nResponse } from 'utils/index';
import { ContextConfigContent } from 'utils/gloo-types';
import { SelectOption } from 'utils/frontendTypes';

import Button from 'components/atoms/Button';
import Checkbox from 'components/atoms/Checkbox';
import HelpPopover from 'components/molecules/HelpPopover';
import Input from 'components/molecules/Input';
import Select from 'components/molecules/Select';
import Typography from 'components/atoms/Typography';
import { useIsRecovery } from 'utils/reduxHook';

interface ContentQuestionsProps {
  content: ContextConfigContent;
  onUpdateContent: (content: ContextConfigContent) => void;
}

type Question = ContextConfigContent['questions'][0];
const defaultQuestion: Question = { text: '', metricId: 0, captureForUsersGroups: false };

function SurveyQuestions({ content, onUpdateContent }: ContentQuestionsProps) {
  const { isRecoveryOrganization } = useIsRecovery();

  const questions = content?.questions || [];

  const metricsOptions = useMemo(
    () => (content?.metrics || []).map((m) => ({ label: m.label, value: m.id })),
    [content?.metrics],
  );

  const updateQuestion = (index: number, data: Partial<Question>) => {
    return questions.map((eq, index2) => (index === index2 ? { ...eq, ...data } : eq));
  };

  const addNewQuestion = () => {
    return [...(questions || []), defaultQuestion];
  };

  return (
    <div className="mt-5">
      <Typography className="pl-1 mb-1 text-blackDark" variant="label">
        What questions do you want to ask?
        <HelpPopover inline className="ml-1" size={14} content="Questions require that you use Metrics." />
      </Typography>
      <div className="flex flex-col justify-center pl-1">
        {questions?.map((q, index) => (
          <div key={`${index}`} className="flex flex-col mb-3">
            <div className="flex flex-col w-full">
              <Typography variant="label">{`Question ${index + 1}`}</Typography>
              <div className="flex flex-row mt-1 w-full">
                <Input
                  placeholder={`Question ${index + 1}`}
                  rows={2}
                  isMultiLine
                  characterLimit={Configs.Sms.CharCount2}
                  emojiCharacterLimit={Configs.Sms.EmojiCharCount2}
                  maxLength={Configs.Sms.CharCount2}
                  displayMessageCredits={isRecoveryOrganization}
                  onBlur={(e) =>
                    onUpdateContent({ ...content, questions: updateQuestion(index, { text: e?.target?.value || '' }) })
                  }
                  defaultValue={getI18nResponse(q.text)}
                />
                <div className="w-48 ml-2">
                  <Select
                    dataDurableId="surveyQuestionsSelectId"
                    options={metricsOptions}
                    value={metricsOptions.find((mo) => mo.value === q.metricId)}
                    onChange={(option: SelectOption) =>
                      onUpdateContent({
                        ...content,
                        questions: updateQuestion(index, { metricId: option?.value || 0 }),
                      })
                    }
                    className="border border-blueLightest rounded shadow"
                  />
                </div>
                <div className="ml-4 mt-3">
                  <Button
                    onClick={() =>
                      onUpdateContent({
                        ...content,
                        questions: questions.filter((eq, index2) => index !== index2),
                      })
                    }
                    type="transparent"
                    dataDurableId="surveyUpdateQuestionsButton"
                    icon="trash-2"
                    iconSize={14}
                    className="text-red"
                  />
                </div>
              </div>
            </div>
            <Checkbox
              label={
                <div className="text-sm text-thryveGray flex items-center">
                  {`Capture question ${index + 1} separately for each group? `}
                  <HelpPopover
                    inline
                    className="ml-1"
                    size={14}
                    content="This will get people to report for each group that is included in the groups selected up above. If no groups are selected, or the person is not a part of any those groups, they will not get a chance to report/answer for each group."
                  />
                </div>
              }
              checked={!!q.captureForUsersGroups}
              onChange={() =>
                onUpdateContent({
                  ...content,
                  questions: updateQuestion(index, { captureForUsersGroups: !q.captureForUsersGroups }),
                })
              }
            />
          </div>
        ))}
        <div>
          <Button
            onClick={() => onUpdateContent({ ...content, questions: addNewQuestion() })}
            text="Add New Question"
            disabled={content?.metrics?.length < 1 || questions.length >= Configs.ContextConfigs.MaxQuestions}
            type="secondary"
            icon="plus"
            dataDurableId="surveyAddNewQuestionButton"
            size="small"
            className="mt-1"
            data-analytics="survey-add-new-question"
          />
        </div>
      </div>
    </div>
  );
}

export default SurveyQuestions;
